import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Session, SupportContainer } from "./components/Session";
import InstituteBrand from "./components/InstituteBrand";
import { useSelector, useDispatch } from "react-redux";
import {
  setSelectedSession,
  setSessionMonth,
} from "app/modules/schools/operations";
import {
  selectSession,
  selectActiveSession,
  userDetailSelector,
} from "app/modules/schools/selectors";
import { userDocIdSelector } from "app/modules/auth/selectors";
import { getLoggedInUserDetail } from "app/modules/schools/thunk";
import { generateNameInitial } from "app/utils/helper";
import {
  Box,
  Container,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { userPrimaryRoleSelector } from "app/modules/schools/selectors";

import UserProfileDrawer from "./components/UserProfileDrawer";

import dayjs from "dayjs";
import { getAllSessions } from "app/services/management";
import { toast } from "react-toastify";
import { useQuery } from "react-query";
import SettingModal from "./SettingsModal";
import { useNavigate } from "react-router-dom";
import { CurrencyRupee, Home, Settings } from "@mui/icons-material";

const Header = ({ handleOnClickHamburgeMenu }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const dispatch = useDispatch();
  const primaryRole = useSelector(userPrimaryRoleSelector);
  const selectedSession = useSelector(selectActiveSession);
  const userDocId = useSelector(userDocIdSelector);
  const userDetail = useSelector(userDetailSelector);
  const sessionlist = useSelector(selectSession);
  // const semesterList = useSelector(selectSemester);
  const [state, setState] = useState({
    showSidebar: false,
  });
  const [settingModalOpen, setSettingModalOpen] = useState(false);

  const handleSettingModal = () => {
    setSettingModalOpen((prev) => !prev);
  };
  const handleSessionSelection = (session) => {
    dispatch(setSelectedSession({ sessionName: session }));
  };

  useQuery({
    queryKey: ["getAllSessionsInHeader", selectedSession],
    queryFn: () => getAllSessions(),
    onSuccess: (success) => {
      const filteredData = success?.data?.list?.find(
        (item) => item?.sessionName === selectedSession
      );

      if (filteredData !== undefined) {
        const sessionStartDate = dayjs(filteredData.startDate);
        const sessionEndDate = dayjs(filteredData.endDate);
        const months = [];
        const dt = new Date(sessionStartDate);
        for (
          let index = 0;
          index < sessionEndDate.diff(sessionStartDate, "M") + 1;
          index++
        ) {
          const month =
            dt.getMonth() + 1 < 10
              ? `0${dt.getMonth() + 1}`
              : dt.getMonth() + 1;

          months.push(
            dayjs(new Date(`${month}-01-${dt.getFullYear()}`)).format("MMMM")
          );
          dt.setMonth(dt.getMonth() + 1);
        }
        dispatch(setSessionMonth(months));
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
    enabled: selectedSession ? true : false,
  });

  // Fetch user Details
  useEffect(() => {
    if (userDocId) {
      dispatch(getLoggedInUserDetail({ staffDocId: userDocId }));
    }
  }, [userDocId, dispatch]);

  const navigate = useNavigate();

  return (
    <StyledContainer>
      <UserProfileDrawer state={state} setState={setState} />
      <SettingModal open={settingModalOpen} onClose={handleSettingModal} />

      <div className="navbar-bg-NX">
        <div className="inner-nav">
          <Container
            maxWidth={false}
            sx={{
              px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
              maxWidth: "100%",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={{ xs: 0.75, sm: 1, md: 1.25, lg: 1.5 }}
              sx={{ height: "100%", width: "100%" }}
            >
              {/* Left Section - Institute Brand */}
              <Box
                sx={{
                  flex: "0 0 auto",
                  minWidth: 0,
                  maxWidth: {
                    xs: "180px",
                    sm: "220px",
                    md: "280px",
                    lg: "350px",
                  },
                }}
              >
                <InstituteBrand onClick={handleOnClickHamburgeMenu} />
              </Box>

              {/* Middle Section - Session & Actions */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={{ xs: 1, sm: 1.5, md: 2, lg: 2.5 }}
                sx={{
                  flex: "1 1 auto",
                  justifyContent: "center",
                  display: { xs: "none", sm: "flex" },
                  minWidth: 0,
                }}
              >
                {/* Session + Support Group */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={{ xs: 0.75, sm: 1, md: 1.25 }}
                  sx={{
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: {
                        xs: "-6px",
                        sm: "-9px",
                        md: "-12px",
                        lg: "-15px",
                      },
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(8, 76, 132, 0.15)",
                      display: { xs: "none", md: "block" },
                    },
                  }}
                >
                  <Session
                    handleSessionSelection={handleSessionSelection}
                    selectedSession={selectedSession}
                    sessions={sessionlist}
                  />

                  <SupportContainer />
                </Stack>

                {/* Action Buttons Group */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={{ xs: 0.75, sm: 1 }}
                >
                  <Tooltip title="Dashboard" arrow placement="bottom">
                    <IconButton
                      onClick={() => navigate("/dashboard")}
                      sx={{
                        backgroundColor: settingModalOpen
                          ? "#e8f0fe"
                          : "transparent",
                        border: "1px solid rgba(60, 110, 239, 0.5)",
                        borderRadius: "10px",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor: "#e8f0fe",
                          border: "1px solid rgba(60, 110, 239, 0.5)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Home fontSize="medium" sx={{ color: "#084d85" }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Settings" arrow placement="bottom">
                    <IconButton
                      onClick={handleSettingModal}
                      sx={{
                        backgroundColor: settingModalOpen
                          ? "#e8f0fe"
                          : "transparent",
                        border: "1px solid rgba(60, 110, 239, 0.5)",
                        borderRadius: "10px",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor: "#e8f0fe",
                          border: "1px solid rgba(60, 110, 239, 0.5)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Settings
                        fontSize="medium"
                        sx={{
                          transform: settingModalOpen
                            ? "rotate(90deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.3s ease-in-out",
                          color: "#084d85",
                        }}
                      />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Payment" arrow placement="bottom">
                    <IconButton
                      onClick={() => navigate("/schools/school-profile?tab=transaction")}
                      sx={{
                        backgroundColor: settingModalOpen
                          ? "#e8f0fe"
                          : "transparent",
                        border: "1px solid rgba(60, 110, 239, 0.5)",
                        borderRadius: "10px",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor: "#e8f0fe",
                          border: "1px solid rgba(60, 110, 239, 0.5)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CurrencyRupee
                        fontSize="medium"
                        sx={{ color: "#084d85" }}
                      />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              {/* Right Section - User Profile */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={{ xs: 0.75, sm: 1, md: 1.25 }}
                sx={{ flex: "0 0 auto", minWidth: 0 }}
              >
                {!isMobile && (
                  <ProfileNameWrapper>
                    <p
                      className="profile-name"
                      title={`${userDetail?.firstName ?? ""} ${
                        userDetail?.middleName ?? ""
                      } ${userDetail?.lastName ?? ""}`}
                    >
                      {userDetail?.firstName} {userDetail?.middleName ?? ""}{" "}
                      {userDetail?.lastName ?? ""}
                    </p>
                    <p className="role" style={{ textTransform: "capitalize" }}>
                      {primaryRole ?? ""}
                    </p>
                  </ProfileNameWrapper>
                )}
                <Tooltip title="Profile" arrow>
                  <AvatarWrapper
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        showSidebar: !prev.showSidebar,
                      }))
                    }
                  >
                    {userDetail?.profilePic ? (
                      <img
                        alt="profile-pic"
                        src={userDetail?.profilePic}
                        className="profile-avatar"
                      />
                    ) : (
                      <div className="avatar-initials">
                        {generateNameInitial(
                          userDetail?.firstName,
                          userDetail?.lastName
                        )}
                      </div>
                    )}
                  </AvatarWrapper>
                </Tooltip>
              </Stack>
            </Stack>
          </Container>
        </div>
      </div>
    </StyledContainer>
  );
};

export default Header;

const ProfileNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  max-width: 180px;
  min-width: 120px;

  .profile-name {
    font-size: 15px;
    font-weight: 600;
    color: #0c2f49;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    text-align: right;
    line-height: 1.3;
    margin: 0;
  }

  .role {
    font-size: 12px;
    font-weight: 400;
    color: #5a7184;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    text-align: right;
    line-height: 1.3;
    margin: 0;
  }

  @media (max-width: 1440px) {
    max-width: 150px;
    min-width: 100px;
  }
`;

const AvatarWrapper = styled.div`
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
  }

  .profile-avatar {
    height: 48px;
    width: 48px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #3c6eef;
    box-shadow: 0 2px 8px rgba(60, 110, 239, 0.2);
    transition: all 0.3s ease;
    display: block;

    @media (max-width: 1024px) {
      height: 42px;
      width: 42px;
    }

    @media (max-width: 768px) {
      height: 38px;
      width: 38px;
    }
  }

  .avatar-initials {
    height: 48px;
    width: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3c6eef 0%, #5b8def 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 16px;
    box-shadow: 0 2px 8px rgba(60, 110, 239, 0.2);
    border: 2px solid #e8f0fe;
    transition: all 0.3s ease;

    @media (max-width: 1024px) {
      height: 42px;
      width: 42px;
      font-size: 14px;
    }

    @media (max-width: 768px) {
      height: 38px;
      width: 38px;
      font-size: 13px;
    }
  }

  &:hover .profile-avatar,
  &:hover .avatar-initials {
    box-shadow: 0 4px 16px rgba(60, 110, 239, 0.3);
  }
`;

const StyledContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;
  max-width: 100vw;
  overflow: hidden;

  .MuiBadge-badge {
    height: 14px !important;
    width: 14px !important;
    border-radius: 7px !important;
  }

  .navbar-brand {
    svg {
      width: 200px;

      @media (max-width: 1024px) {
        width: 150px;
      }

      @media (max-width: 768px) {
        width: 120px;
      }
    }
  }

  .navbar-bg-NX {
    background: #084c84;
    padding: 0;
    margin: 0;
    transition: all 0.3s ease;
  }

  .inner-nav {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    width: 100%;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    height: 70px;
    display: flex;
    align-items: center;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;

    @media (max-width: 1024px) {
      height: 64px;
    }

    @media (max-width: 768px) {
      height: 60px;
    }
  }

  .text-color {
    color: #084c84;
  }
`;
